import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import RouteSeo from "./RouteSeo";
import CardFX from "./components/CardFX";
import HomePage from "./pages/HomePage";
import PageView from "./pages/PageView";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import SystemDetailPage from "./pages/SystemDetailPage";
import LoginPage from "./pages/LoginPage";
import AdminLayout from "./admin/AdminLayout";
import AdminDashboard from "./admin/AdminDashboard";
import AdminProfile from "./admin/AdminProfile";
import PageSettings from "./admin/PageSettings";
import HeroCarouselSettings from "./admin/home/HeroCarouselSettings";
import BrowserTitleSettings from "./admin/home/BrowserTitleSettings";
import ServicesSettings from "./admin/home/ServicesSettings";
import SubServicesSettings from "./admin/home/SubServicesSettings";
import SystemsSettings from "./admin/home/SystemsSettings";
import SystemPageEditor from "./admin/home/SystemPageEditor";
import QuoteRequestsAdmin from "./admin/contact/QuoteRequestsAdmin";
import AIToolsCategoriesSettings from "./admin/ai-tools/AIToolsCategoriesSettings";
import SiteIdentitySettings from "./admin/settings/SiteIdentitySettings";
import NavbarSettings from "./admin/settings/NavbarSettings";
import FooterSettings from "./admin/settings/FooterSettings";
import ProtectedRoute from "./admin/ProtectedRoute";
import CoWorkPage from "./pages/CoWorkPage";
import CoWorkJobPage from "./pages/CoWorkJobPage";
import CoWorkDashboardPage from "./pages/CoWorkDashboardPage";
import CoWorkMessagesPage from "./pages/CoWorkMessagesPage";
import CoWorkAssistantPage from "./pages/CoWorkAssistantPage";
import CoWorkSellerPage from "./pages/CoWorkSellerPage";
import FoundationPage from "./pages/FoundationPage";
import FoundationRegisterPage from "./pages/FoundationRegisterPage";
import FoundationCasePage from "./pages/FoundationCasePage";
import PortfolioPage from "./pages/PortfolioPage";
import PortfolioProjectPage from "./pages/PortfolioProjectPage";
import BlogPostPage from "./pages/BlogPostPage";
import BlogPostsAdmin from "./admin/blogs/BlogPostsAdmin";
import ChatbotsAdmin from "./admin/chatbots/ChatbotsAdmin";
import CoWorkBrandingSettings from "./admin/cowork/CoWorkBrandingSettings";
import CoWorkSlidesSettings from "./admin/cowork/CoWorkSlidesSettings";
import CoWorkContentSettings from "./admin/cowork/CoWorkContentSettings";
import CoWorkJobsAdmin from "./admin/cowork/CoWorkJobsAdmin";
import CoWorkMembersAdmin from "./admin/cowork/CoWorkMembersAdmin";
import FoundationRequestsAdmin from "./admin/foundation/FoundationRequestsAdmin";
import HelpOffersAdmin from "./admin/foundation/HelpOffersAdmin";
import ContactMessagesAdmin from "./admin/contact/ContactMessagesAdmin";
import FoundationContentSettings from "./admin/foundation/FoundationContentSettings";
import ProjectsSettings from "./admin/portfolio/ProjectsSettings";
import { fetchSiteIdentity } from "./api";
import './App.css';

// The browser tab favicon is global to the whole SPA (public site + admin panel share one tab),
// so it's set once here rather than per-page.
function SiteFavicon() {
  useEffect(() => {
    fetchSiteIdentity()
      .then((identity) => {
        if (!identity.favicon) return;
        let link = document.querySelector("link[rel='icon']");
        if (!link) {
          link = document.createElement("link");
          link.rel = "icon";
          document.head.appendChild(link);
        }
        link.href = identity.favicon;
      })
      .catch(() => {});
  }, []);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <SiteFavicon />
      <RouteSeo />
      <CardFX />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services/:id" element={<ServiceDetailPage />} />
        <Route path="/system/:id" element={<SystemDetailPage />} />

        <Route path="/co-work" element={<CoWorkPage />} />
        <Route path="/co-work/jobs/:id" element={<CoWorkJobPage />} />
        <Route path="/co-work/dashboard" element={<CoWorkDashboardPage />} />
        <Route path="/co-work/messages" element={<CoWorkMessagesPage />} />
        <Route path="/co-work/assistant" element={<CoWorkAssistantPage />} />
        <Route path="/co-work/sellers/:id" element={<CoWorkSellerPage />} />
        <Route path="/foundation" element={<FoundationPage />} />
        <Route path="/foundation/register" element={<FoundationRegisterPage />} />
        <Route path="/foundation/:id" element={<FoundationCasePage />} />
        <Route path="/portfolio" element={<PortfolioPage />} />
        <Route path="/portfolio/:id" element={<PortfolioProjectPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />

        <Route path="/login" element={<LoginPage />} />
        <Route path="/admin/login" element={<Navigate to="/login" replace />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="profile" element={<AdminProfile />} />
          <Route path="settings/site-identity" element={<SiteIdentitySettings />} />
          <Route path="settings/navbar" element={<NavbarSettings />} />
          <Route path="settings/footer" element={<FooterSettings />} />
          <Route path="pages/home" element={<BrowserTitleSettings />} />
          <Route path="pages/home/hero-carousel" element={<HeroCarouselSettings />} />
          <Route path="pages/home/services" element={<ServicesSettings />} />
          <Route path="pages/home/sub-services" element={<SubServicesSettings />} />
          <Route path="pages/home/systems" element={<SystemsSettings />} />
          <Route path="pages/home/systems/:id/page" element={<SystemPageEditor />} />
          <Route path="pages/contact/quotes" element={<QuoteRequestsAdmin />} />
          <Route path="pages/ai-tools/categories" element={<AIToolsCategoriesSettings />} />
          <Route path="pages/ai-tools/chatbots" element={<ChatbotsAdmin />} />
          <Route path="pages/co-work" element={<CoWorkBrandingSettings />} />
          <Route path="pages/co-work/slides" element={<CoWorkSlidesSettings />} />
          <Route path="pages/co-work/content" element={<CoWorkContentSettings />} />
          <Route path="pages/co-work/jobs" element={<CoWorkJobsAdmin />} />
          <Route path="pages/co-work/members" element={<CoWorkMembersAdmin />} />
          <Route path="pages/foundation/requests" element={<FoundationRequestsAdmin />} />
          <Route path="pages/foundation/offers" element={<HelpOffersAdmin />} />
          <Route path="pages/foundation/content" element={<FoundationContentSettings />} />
          <Route path="pages/contact/messages" element={<ContactMessagesAdmin />} />
          <Route path="pages/portfolio/projects" element={<ProjectsSettings />} />
          <Route path="pages/blogs/posts" element={<BlogPostsAdmin />} />
          <Route path="pages/:slug" element={<PageSettings />} />
        </Route>

        <Route path="/:slug" element={<PageView />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
